import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import styles from './StackedBarChart.module.scss';
import { useTranslation } from 'react-i18next';
import { CurrencyInputFormatter } from '../../../utils/currencyFormatter';
import { currencyLocaleMappings } from '../../../constants/dataConstants';
import { NumberFormatter } from '../../../utils/NumberFormatter';

export interface StackedBarChartSegment {
  label: string;
  value: number;
  color?: string;
}

export interface StackedBarChartData {
  label: string;
  segments: StackedBarChartSegment[];
}

interface StackedBarChartProps {
  data: StackedBarChartData[];
  currency?: string;
  className?: string;
  barWidth?: number;
  yAxisSteps?: number;
  yAxisValues?: number[];
  affiliateCode?: string;
}

const PALETTE_LENGTH = 13; // standard data-vis palette length

const StackedBarChart: React.FC<StackedBarChartProps> = ({
  data,
  currency,
  affiliateCode,
  className,
  barWidth = 16,
  yAxisSteps = 4,
  yAxisValues = [],
}) => {
  const { t, i18n } = useTranslation();

  // 1. Collect all unique segment labels to assign consistent colors
  const segmentLabels = useMemo(() => {
    const labels = new Set<string>();
    data?.forEach((item) => item?.segments?.forEach((segment) => labels?.add(segment.label)));
    return Array.from(labels);
  }, [data]);

  // 2. Map labels to colors
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    segmentLabels?.forEach((label, index) => {
      map[label] = `var(--data-vis-data-vis-${(index % PALETTE_LENGTH) + 1})`;
    });
    return map;
  }, [segmentLabels]);

  // 3. Process data: Sort segments ASC (smallest to largest) for stacking
  //    and calculate DELTA for rendering height.
  const processedData = useMemo(() => {
    return data.map((d) => {
      const currentSegments = [...d.segments];
      segmentLabels.forEach((label) => {
        if (!currentSegments.find((segment) => segment.label === label)) {
          currentSegments.push({ label, value: 0 });
        }
      });

      const sortedSegments = currentSegments.sort((a, b) => a.value - b.value);
      // Total height of the stack is the value of the largest segment (cumulative top)
      const total = sortedSegments.length > 0 ? sortedSegments[sortedSegments.length - 1].value : 0;

      const segmentsWithDelta = sortedSegments.map((s, i) => {
        const prevValue = i === 0 ? 0 : sortedSegments[i - 1].value;
        const delta = s.value - prevValue;
        return {
          ...s,
          color: s.color || colorMap[s.label],
          delta: Math.max(0, delta), // Visual height component
        };
      });

      return { ...d, segments: segmentsWithDelta, total };
    });
  }, [data, colorMap, segmentLabels]);

  // 4. Determine Y-axis scale
  const maxTotal = useMemo(() => {
    if (yAxisValues && yAxisValues.length > 0) {
      return Math.max(...yAxisValues);
    }

    const max = Math.max(...processedData.map((d) => d.total), 0);
    if (max === 0) return 100;

    const roughStep = max / yAxisSteps;
    // Calculate step value (multiple of 50)
    const niceStep = Math.ceil(roughStep / 50) * 50;
    // The new max for the axis is a multiple of the step
    return niceStep * yAxisSteps;
  }, [processedData, yAxisSteps, yAxisValues]);

  // Generate Y-axis ticks based on the yAxisSteps prop
  const yTicks = useMemo(() => {
    if (yAxisValues && yAxisValues.length > 0) {
      return [...yAxisValues].sort((a, b) => b - a);
    }

    const ticks = [];
    for (let i = 0; i <= yAxisSteps; i++) {
      ticks.push((maxTotal / yAxisSteps) * i);
    }
    return ticks.reverse(); // Top to bottom for rendering
  }, [maxTotal, yAxisSteps, yAxisValues]);

  // Tooltip state
  const [hoveredSegment, setHoveredSegment] = useState<{
    label: string;
    value: number;
    color: string;
    barLabel: string;
  } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipStyle, setTooltipStyle] = useState({ left: 0, top: 0, arrowTop: '50%' });
  const [placement, setPlacement] = useState<'left' | 'right'>('right');
  const tooltipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (hoveredSegment && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const { x, y } = tooltipPosition;
      const width = rect.width;
      const height = rect.height;
      const padding = 20; // safe distance from screen edges
      const offset = 16; // distance from cursor

      // 1. Horizontal Placement (Right preferred)
      let left = x + offset;
      let place: 'left' | 'right' = 'right';

      if (left + width > screenWidth - padding) {
        // Flip to left
        left = x - width - offset;
        place = 'left';
      }
      // Clamp horizontal if needed (rare)
      if (left < padding) left = padding;

      // 2. Vertical Placement (Center on cursor, but clamp to screen)
      let top = y - height / 2;

      // Clamp Vertical
      if (top < padding) {
        top = padding;
      } else if (top + height > screenHeight - padding) {
        top = screenHeight - padding - height;
      }

      // 3. Arrow Placement (Follow cursor Y relative to tooltip)
      // Arrow points to 'y' (cursor Y)
      let arrowTopVal = y - top;

      const arrowMargin = 12;
      if (arrowTopVal < arrowMargin) arrowTopVal = arrowMargin;
      if (arrowTopVal > height - arrowMargin) arrowTopVal = height - arrowMargin;

      setTooltipStyle({
        left,
        top,
        arrowTop: `${arrowTopVal}px`,
      });
      setPlacement(place);
    }
  }, [hoveredSegment, tooltipPosition]);

  const handleMouseMove = (
    e: React.MouseEvent,
    segment: StackedBarChartSegment,
    barLabel: string
  ) => {
    setHoveredSegment({ ...segment, barLabel, color: segment.color! });
    setTooltipPosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredSegment(null);
  };

  const formatYLabel = (val: number) => {
    const locale = currency ? currencyLocaleMappings[currency] : 'en-US';

    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    }).format(Math.round(val));
  };

  // Legend items (unique)
  const legendItems = segmentLabels.map((label) => ({
    label,
    color: colorMap[label],
  }));

  if (!data || data.length === 0)
    return <div className={styles.no_data}>{t('noDataAvailable')}</div>;

  return (
    <div className={`${styles.chart_container} ${className || ''}`}>
      <div className={styles.chart_area}>
        {/* Y Axis */}
        <div className={styles.y_axis}>
          {yTicks.map((tick, i) => (
            <div key={i} className={styles.y_label}>
              {formatYLabel(tick)}
            </div>
          ))}
        </div>

        {/* Grid Lines */}
        <div className={styles.grid_lines}>
          {yTicks.map((_, i) => (
            <div key={i} className={styles.grid_line} />
          ))}
        </div>

        {/* Bars */}
        <div className={styles.bars_container}>
          {processedData.map((bar, i) => (
            <div key={i} className={styles.bar_group}>
              <div
                className={styles.bar_stack}
                style={
                  {
                    height: `${maxTotal > 0 ? (bar.total / maxTotal) * 100 : 0}%`,
                    '--animation-delay': `${i * 150}ms`,
                    '--bar-width': `${barWidth}px`,
                  } as React.CSSProperties
                }
              >
                {/* Segments sorted ASC (smallest value first) are rendered. 
                    flex-direction: column-reverse puts first item (smallest) at bottom.
                */}
                {bar.segments.map((seg, j) => {
                  // Use DELTA for height, but keep SEG.VALUE for tooltip logic if needed
                  const heightPercent = bar.total > 0 ? (seg.delta / bar.total) * 100 : 0;
                  return (
                    <div
                      key={j}
                      className={styles.bar_segment}
                      style={{
                        height: `${heightPercent}%`,
                        minHeight: '5px',
                        backgroundColor: seg.color,
                      }}
                      onMouseMove={(e) => handleMouseMove(e, seg, bar.label)}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}
              </div>
              <div className={styles.x_label}>{bar.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className={styles.legend_container}>
        {legendItems.map((item, i) => (
          <div key={i} className={styles.legend_item}>
            <div className={styles.legend_dot} style={{ backgroundColor: item.color }} />
            <div>
              {i18n.exists(`charts_tables_dynamic_labels.${item.label}`)
                ? t(`charts_tables_dynamic_labels.${item.label}`)
                : item.label}
            </div>
          </div>
        ))}
      </div>

      {/* Tooltip */}
      {hoveredSegment && (
        <div
          ref={tooltipRef}
          className={`${styles.tooltip} ${placement === 'left' ? styles.place_left : styles.place_right}`}
          style={{ left: tooltipStyle.left, top: tooltipStyle.top }}
        >
          <div className={styles.tooltip_title}>{hoveredSegment.barLabel}</div>
          <div className={styles.tooltip_value}>
            <span>
              {i18n.exists(`charts_tables_dynamic_labels.${hoveredSegment.label}`)
                ? t(`charts_tables_dynamic_labels.${hoveredSegment.label}`)
                : hoveredSegment.label}
              :
            </span>
            <span>
              {currency
                ? CurrencyInputFormatter(currency, hoveredSegment.value)
                : affiliateCode
                  ? NumberFormatter(affiliateCode, hoveredSegment.value)
                  : hoveredSegment.value}
            </span>
          </div>
          <style>{`
                .${styles.tooltip}::after { top: ${tooltipStyle.arrowTop}; margin-top: -6px; }
            `}</style>
        </div>
      )}
    </div>
  );
};

export default StackedBarChart;
